import { nextTick, onUnmounted, ref, unref, watch } from 'vue';
import type { DynamicProps, Nullable } from '@mts-fe/types';
import type { NamePath } from 'ant-design-vue/es/form/interface';
import { getDynamicProps } from '@mts-fe/utils';
import { error } from '@mts/utils';
import { IN_PROD } from '@mts/utils/env';
import type {
  FormActionType,
  FormProps,
  FormSchemaInner as FormSchema,
  SetFieldsValueOptions,
} from '../types/form';

export function useForm(
  props?: Partial<DynamicProps<FormProps>>,
): [(formInstance: FormActionType) => void, FormActionType] {
  const formRef = ref<Nullable<FormActionType>>(null);
  const loadedRef = ref<Nullable<boolean>>(false);

  async function getForm() {
    const form = unref(formRef);
    if (!form) {
      error(
        'The form instance has not been obtained, please make sure that the form has been rendered when performing the form operation!',
      );
    }
    await nextTick();
    return form as FormActionType;
  }

  function register(instance: FormActionType) {
    IN_PROD &&
      onUnmounted(() => {
        formRef.value = null;
        loadedRef.value = null;
      });
    if (unref(loadedRef) && IN_PROD && instance === unref(formRef)) return;

    formRef.value = instance;
    loadedRef.value = true;

    // if (PADMode) return;
    watch(
      () => props,
      () => {
        props && instance.setProps(getDynamicProps(props));
      },
      {
        immediate: true,
        deep: true,
      },
    );
  }

  const methods: FormActionType = {
    scrollToField: async (name: NamePath, options?: ScrollOptions | undefined) => {
      const form = await getForm();
      form.scrollToField(name, options);
    },
    focusByField: (field: string, ...args) => {
      unref(formRef)?.focusByField(field, ...args);
    },
    setProps: async (formProps: Partial<FormProps>) => {
      const form = await getForm();
      form.setProps(formProps);
    },

    updateRelateFields: async (field: string, value: any, schema?: FormSchema) => {
      unref(formRef)?.updateRelateFields(field, value, schema);
    },

    refreshSchemas: (fields?: string[]) => {
      unref(formRef)?.refreshSchemas(fields);
    },

    updateSchema: async (data: Partial<FormSchema> | Partial<FormSchema>[]) => {
      const form = await getForm();
      form.updateSchema(data);
    },

    resetSchema: async (data: Partial<FormSchema> | Partial<FormSchema>[]) => {
      const form = await getForm();
      form.resetSchema(data);
    },

    clearValidate: async (name?: string | string[]) => {
      const form = await getForm();
      form.clearValidate(name);
    },

    resetFields: (noSubmit?: boolean | PointerEvent) => {
      // 修复表单重置后，页面变化了，但是由于异步问题导致表单内部的状态没有及时同步
      return new Promise((resolve) => {
        getForm().then(async (form) => {
          await form.resetFields(noSubmit);
          resolve();
        });
      });
    },

    removeSchemaByField: async (field: string | string[]) => {
      unref(formRef)?.removeSchemaByField(field);
    },

    // TODO promisify
    getFieldsValue: <T>() => {
      return unref(formRef)?.getFieldsValue() as T;
    },

    setFieldsValue: async <T extends Recordable<any>>(values: T, opt?: SetFieldsValueOptions) => {
      const form = await getForm();
      form.setFieldsValue(values, opt);
    },

    appendSchemaByField: async (
      schema: FormSchema | FormSchema[],
      prefixField: string | undefined,
      first?: boolean,
    ) => {
      const form = await getForm();
      form.appendSchemaByField(schema, prefixField, first);
    },

    submit: async (): Promise<any> => {
      const form = await getForm();
      return form.submit();
    },

    validate: async <T = Recordable>(nameList?: NamePath[] | false): Promise<T> => {
      const form = await getForm();
      return form.validate(nameList);
    },

    validateFields: async (nameList?: NamePath[]): Promise<Recordable> => {
      const form = await getForm();
      return form.validateFields(nameList);
    },
    resetDefaultField: async (nameList?: NamePath[]) => {
      unref(formRef)?.resetDefaultField(nameList);
    },
  };

  return [register, methods];
}
